import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Circle, ListChecks } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { OutstandingField, Task } from '../lib/types';
import { useSectionFigures } from './FigureCard';
import { formatDueDate, isOpenTask, useTasks, useTaskUpdate } from './TaskCard';
import WidgetCard from './WidgetCard';

const SHOWN = 5;

const rowClass = 'group flex items-center gap-3 py-2.5 hover:text-brand-dark transition-colors';

// The workspace's next steps across all eight business areas: its open tasks,
// what its figures need, then the questions the record can't answer yet. Anything
// a connected source already establishes never appears, so the list shrinks as
// much by connecting things as by typing.
export default function NextSteps({ limit = SHOWN }: { limit?: number }) {
    const { authFetch } = useAuth();
    const { sections } = useWorkspace();
    const [questions, setQuestions] = useState<OutstandingField[] | null>(null);
    const { tasks, reload } = useTasks();
    const { inputs } = useSectionFigures();

    useEffect(() => {
        authFetch(`/workspace/fields/outstanding?only_unknown=true&limit=${limit}`)
            .then(r => r.ok ? r.json() : [])
            .then(setQuestions)
            .catch(() => setQuestions([]));
    }, [authFetch, limit]);

    if (!questions || !tasks || !inputs) return null;
    const open = tasks.filter(isOpenTask).slice(0, limit);
    const needed = inputs.slice(0, Math.max(0, limit - open.length));
    const asked = questions.slice(0, Math.max(0, limit - open.length - needed.length));
    if (open.length === 0 && asked.length === 0 && needed.length === 0) return null;

    const nameOf = (id: string | null) => {
        if (!id) return '';
        const top = id.split('.')[0];
        const find = (list: any[]): string => {
            for (const s of list ?? []) {
                if (s.id === id) return s.name;
                const hit = find(s.subsections ?? []);
                if (hit) return hit;
            }
            return '';
        };
        return find(sections ?? []) || top.replace(/_/g, ' ');
    };

    return (
        <WidgetCard title="What's next" icon={ListChecks}>
            <ul className="divide-y divide-softgrey/60">
                {open.map(t => <TaskRow key={t.id} task={t} area={nameOf(t.section_id)} onChanged={reload} />)}
                {needed.map(i => (
                    <li key={i.id}>
                        <Link to={`/workspace/${i.section_id}?tab=next`} className={rowClass}>
                            <span className="flex-1 min-w-0">
                                <span className="block text-[13px] text-graphite truncate">{i.title}</span>
                                <span className="block text-[11px] text-coolslate truncate">For {i.figures.join(', ')}</span>
                            </span>
                            <ArrowRight className="w-4 h-4 text-coolslate opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        </Link>
                    </li>
                ))}
                {asked.map(q => (
                    <li key={q.id}>
                        <Link to={`/workspace/${q.section_id ?? ''}?field=${encodeURIComponent(q.id)}`} className={rowClass}>
                            <span className="flex-1 min-w-0">
                                <span className="block text-[13px] text-graphite truncate">{q.label}</span>
                                <span className="block text-[11px] text-coolslate">{nameOf(q.section_id)}</span>
                            </span>
                            <ArrowRight className="w-4 h-4 text-coolslate opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                        </Link>
                    </li>
                ))}
            </ul>
        </WidgetCard>
    );
}

// A task with no business area has no What's Next tab to open, so it is ticked off here.
function TaskRow({ task, area, onChanged }: { task: Task; area: string; onChanged: () => void }) {
    const { update, busy } = useTaskUpdate(onChanged);
    const detail = [area, task.due_on && `Due ${formatDueDate(task.due_on)}`].filter(Boolean).join(' · ');
    const body = (
        <span className="flex-1 min-w-0">
            <span className="block text-[13px] text-graphite truncate">{task.title}</span>
            {detail && <span className="block text-[11px] text-coolslate">{detail}</span>}
        </span>
    );
    return (
        <li className="flex items-center gap-3">
            <button
                onClick={() => update(task, { status: 'done' })}
                disabled={busy}
                aria-label={`Mark "${task.title}" done`}
                title="Mark done"
                className="text-coolslate hover:text-brand-dark disabled:opacity-40 shrink-0"
            >
                <Circle className="w-4 h-4" />
            </button>
            {task.section_id ? (
                <Link to={`/workspace/${task.section_id}?tab=next`} className={`${rowClass} flex-1 min-w-0`}>
                    {body}
                    <ArrowRight className="w-4 h-4 text-coolslate opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </Link>
            ) : (
                <div className="flex-1 min-w-0 py-2.5">{body}</div>
            )}
        </li>
    );
}
